// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
"use strict";
import { getGlobal, strShimObject, strShimPrototype, strShimUndefined } from "@microsoft/applicationinsights-shims";
import { arrForEach, createCachedValue, getDeferred, getDocument, getInst, getLazy, getNavigator, getPerformance, hasNavigator, isFunction, isNullOrUndefined, isString, isUndefined, mathMax, strIndexOf, strSubstring } from "@nevware21/ts-utils";
import { _DYN_CONCAT, _DYN_CREATE_EVENT, _DYN_JOIN, _DYN_LENGTH, _DYN_NAME, _DYN_PUSH, _DYN_SPLIT, _DYN_SUBSTRING, _DYN_TO_LOWER_CASE, _DYN_USER_AGENT } from "../__DynamicConstants";
import { DEFAULT_SENSITIVE_PARAMS, STR_EMPTY, STR_REDACTED } from "../constants/InternalConstants";
import { strContains } from "./HelperFuncs";
/**
 * This file exists to hold environment utilities that are required to check and
 * validate the current operating environment. Unless otherwise required, please
 * only use defined methods (functions) in this class so that users of these
 * functions/properties only need to include those that are used within their own modules.
 */
var strDocumentMode = "documentMode";
var strLocation = "location";
var strConsole = "console";
var strJSON = "JSON";
var strCrypto = "crypto";
var strMsCrypto = "msCrypto";
var strReactNative = "ReactNative";
var strMsie = "msie";
var strTrident = "trident/";
var strXMLHttpRequest = "XMLHttpRequest";
// Local cached properties which are used to avoid multiple checks within the module
var _isTrident;
var _navUserAgentCheck;
var _enableMocks = false;
var _useXDomainRequest;
var _beaconsSupported;
var _userAgent;
function _hasProperty(theClass, property) {
    var supported = false;
    if (theClass) {
        try {
            supported = property in theClass;
            if (!supported) {
                var proto = theClass[strShimPrototype];
                if (proto) {
                    supported = property in proto;
                }
            }
        }
        catch (e) {
            // Do Nothing
        }
        if (!supported) {
            try {
                var tmp = new theClass();
                supported = !isUndefined(tmp[property]);
            }
            catch (e) {
                // Do Nothing
            }
        }
    }
    return supported;
}
/*#__NO_SIDE_EFFECTS__*/
export function getUserAgentString() {
    if (!_userAgent) {
        // Use lazy to allow mocking
        _userAgent = getLazy(function () {
            var nav = getNavigator() || {};
            return nav[_DYN_USER_AGENT /* @min:%2euserAgent */] || STR_EMPTY;
        });
    }
    return _userAgent.v;
}
/**
 * Enable the lookup of test mock objects if requested
 * @param enabled - A flag to enable or disable the mock
 */
export function setEnableEnvMocks(enabled) {
    _enableMocks = enabled;
}
/**
 * Returns the global location object if it is present otherwise null.
 * This helper is used to access the location object without causing an exception
 * "Uncaught ReferenceError: location is not defined"
 */
/*#__NO_SIDE_EFFECTS__*/
export function getLocation(checkForMock) {
    if (checkForMock && _enableMocks) {
        var mockLocation = getInst("__mockLocation");
        if (mockLocation) {
            return mockLocation;
        }
    }
    if (typeof location === strShimObject && location) {
        return location;
    }
    return getInst(strLocation);
}
/**
 * Returns the global console object
 */
/*#__NO_SIDE_EFFECTS__*/
export function getConsole() {
    if (typeof console !== strShimUndefined) {
        return console;
    }
    return getInst(strConsole);
}
/**
 * Checks if JSON object is available, this is required as we support the API running without a
 * window /document (eg. Node server, electron webworkers) and if we attempt to assign a history
 * object to a local variable or pass as an argument an "Uncaught ReferenceError: JSON is not defined"
 * exception will be thrown.
 * Defined as a function to support lazy / late binding environments.
 */
/*#__NO_SIDE_EFFECTS__*/
export function hasJSON() {
    return Boolean((typeof JSON === strShimObject && JSON) || getInst(strJSON) !== null);
}
/**
 * Returns the global JSON object if it is present otherwise null.
 * This helper is used to access the JSON object without causing an exception
 * "Uncaught ReferenceError: JSON is not defined"
 */
/*#__NO_SIDE_EFFECTS__*/
export function getJSON() {
    if (hasJSON()) {
        return JSON || getInst(strJSON);
    }
    return null;
}
/**
 * Returns the crypto object if it is present otherwise null.
 * This helper is used to access the crypto object from the current
 * global instance which could be window or globalThis for a web worker
 */
/*#__NO_SIDE_EFFECTS__*/
export function getCrypto() {
    return getInst(strCrypto);
}
/**
 * Returns the crypto object if it is present otherwise null.
 * This helper is used to access the crypto object from the current
 * global instance which could be window or globalThis for a web worker
 */
/*#__NO_SIDE_EFFECTS__*/
export function getMsCrypto() {
    return getInst(strMsCrypto);
}
/**
 * Returns whether the environment is reporting that we are running in a React Native Environment
 */
/*#__NO_SIDE_EFFECTS__*/
export function isReactNative() {
    // If running in React Native, navigator.product will be populated
    var nav = getNavigator();
    if (nav && nav.product) {
        return nav.product === strReactNative;
    }
    return false;
}
/**
 * Identifies whether the current environment appears to be IE
 */
/*#__NO_SIDE_EFFECTS__*/
export function isIE() {
    var userAgent = getUserAgentString();
    if (!_isTrident || userAgent !== _navUserAgentCheck) {
        // Added to support test mocking of the user agent
        _navUserAgentCheck = userAgent;
        var lwrUserAgent = _navUserAgentCheck[_DYN_TO_LOWER_CASE /* @min:%2etoLowerCase */]();
        _isTrident = createCachedValue(strContains(lwrUserAgent, strMsie) || strContains(lwrUserAgent, strTrident));
    }
    return _isTrident.v;
}
/**
 * Gets IE version returning the document emulation mode if we are running on IE, or null otherwise
 */
/*#__NO_SIDE_EFFECTS__*/
export function getIEVersion(userAgentStr) {
    if (userAgentStr === void 0) { userAgentStr = null; }
    if (!userAgentStr) {
        userAgentStr = getUserAgentString();
    }
    var ua = (userAgentStr || STR_EMPTY)[_DYN_TO_LOWER_CASE /* @min:%2etoLowerCase */]();
    // Also check for documentMode in case X-UA-Compatible meta tag was included in HTML.
    if (strContains(ua, strMsie)) {
        var doc = getDocument() || {};
        return mathMax(parseInt(ua[_DYN_SPLIT /* @min:%2esplit */](strMsie)[1]), (doc[strDocumentMode] || 0));
    }
    else if (strContains(ua, strTrident)) {
        var tridentVer = parseInt(ua[_DYN_SPLIT /* @min:%2esplit */](strTrident)[1]);
        if (tridentVer) {
            return tridentVer + 4;
        }
    }
    return null;
}
/*#__NO_SIDE_EFFECTS__*/
export function isSafari(userAgentStr) {
    if (!userAgentStr || !isString(userAgentStr)) {
        userAgentStr = getUserAgentString()[_DYN_TO_LOWER_CASE /* @min:%2etoLowerCase */]();
    }
    var ua = (userAgentStr || STR_EMPTY)[_DYN_TO_LOWER_CASE /* @min:%2etoLowerCase */]();
    return (strIndexOf(ua, "safari") >= 0);
}
/**
 * Checks if HTML5 Beacons are supported in the current environment. There is a side effect (for testing)
 * when `useCached` is set to `false`, it will reset the cached value causing all future calls to
 * use the new re-evaluated value for all future calls.
 * @param useCached - [Optional] used for testing to bypass the cached lookup, when `true` this will
 * cause the cached global to be reset.
 * @returns True if supported, false otherwise.
 */
export function isBeaconsSupported(useCached) {
    if (!_beaconsSupported || useCached === false) {
        _beaconsSupported = createCachedValue(hasNavigator() && !!(getNavigator().sendBeacon));
    }
    return _beaconsSupported.v;
}
/**
 * Checks if the Fetch API is supported in the current environment.
 * @param withKeepAlive - [Optional] If True, check if fetch is available and it supports the keepalive feature, otherwise only check if fetch is supported
 * @returns True if supported, otherwise false
 */
/*#__NO_SIDE_EFFECTS__*/
export function isFetchSupported(withKeepAlive) {
    var isSupported = false;
    try {
        isSupported = !!getInst("fetch");
        var request = getInst("Request");
        if (isSupported && withKeepAlive && request) {
            isSupported = _hasProperty(request, "keepalive");
        }
    }
    catch (e) {
        // Just Swallow any failure during availability checks
    }
    return isSupported;
}
/*#__NO_SIDE_EFFECTS__*/
export function useXDomainRequest() {
    if (!_useXDomainRequest) {
        _useXDomainRequest = getDeferred(function () {
            var useXDomainRequest = (typeof XDomainRequest !== strShimUndefined);
            if (useXDomainRequest && isXhrSupported()) {
                useXDomainRequest = useXDomainRequest && !_hasProperty(getInst(strXMLHttpRequest), "withCredentials");
            }
            return !!useXDomainRequest;
        });
    }
    return _useXDomainRequest.v;
}
/**
 * Checks if XMLHttpRequest is supported
 * @returns True if supported, otherwise false
 */
// Do not mark this function as side-effect-free. Rollup can remove capability checks
// guarded by this return value, which prevents XHR instrumentation from being installed.
export function isXhrSupported() {
    var isSupported = false;
    try {
        var xmlHttpRequest = getInst(strXMLHttpRequest);
        isSupported = !!xmlHttpRequest;
    }
    catch (e) {
        // Just Swallow any failure during availability checks
    }
    return isSupported;
}
/*#__NO_SIDE_EFFECTS__*/
function _getNamedValue(values, name) {
    var items = [];
    if (values) {
        arrForEach(values, function (value) {
            if (value[_DYN_NAME /* @min:%2ename */]) {
                if (value[_DYN_NAME /* @min:%2ename */] === name) {
                    items[_DYN_PUSH /* @min:%2epush */](value);
                }
            }
        });
    }
    return items;
}
/**
 * Helper function to fetch the named meta-tag from the page.
 * @param name - The name of the meta-tag to find.
 */
/*#__NO_SIDE_EFFECTS__*/
export function findMetaTag(name) {
    var tags = findMetaTags(name);
    if (tags[_DYN_LENGTH /* @min:%2elength */] > 0) {
        return tags[0];
    }
    return null;
}
/**
 * Helper function to fetch all named meta-tag from the page.
 * @since 3.4.0
 * @param name - The name of the meta-tag to find.
 * @returns - An array of meta-tag values.
 */
/*#__NO_SIDE_EFFECTS__*/
export function findMetaTags(name) {
    var tags = [];
    var doc = getDocument();
    if (doc && name) {
        // Look for a meta-tag
        arrForEach(_getNamedValue(doc.querySelectorAll("meta"), name), function (item) {
            tags[_DYN_PUSH /* @min:%2epush */](item.content);
        });
    }
    return tags;
}
/**
 * Helper function to fetch the named server timing value from the page response (first navigation event).
 * @param name - The name of the server timing value to find.
 */
/*#__NO_SIDE_EFFECTS__*/
export function findNamedServerTiming(name) {
    var value;
    var serverTimings = findNamedServerTimings(name);
    if (serverTimings[_DYN_LENGTH /* @min:%2elength */] > 0) {
        value = serverTimings[0];
    }
    return value;
}
/**
 * Helper function to fetch the named server timing value from the page response (first navigation event).
 * @since 3.4.0
 * @param name - The name of the server timing value to find.
 * @returns - An array of server timing values.
 */
/*#__NO_SIDE_EFFECTS__*/
export function findNamedServerTimings(name) {
    var values = [];
    var perf = getPerformance();
    if (perf && perf.getEntriesByType) {
        // Try looking for a server-timing header
        arrForEach(perf.getEntriesByType("navigation") || [], function (navPerf) {
            arrForEach(_getNamedValue(navPerf.serverTiming, name), function (value) {
                var desc = value.description;
                if (!isNullOrUndefined(desc)) {
                    values[_DYN_PUSH /* @min:%2epush */](desc);
                }
            });
        });
    }
    return values;
}
// TODO: should reuse this method for analytics plugin
export function dispatchEvent(target, evnt) {
    if (target && target.dispatchEvent && evnt) {
        target.dispatchEvent(evnt);
        return true;
    }
    return false;
}
/*#__NO_SIDE_EFFECTS__*/
export function createCustomDomEvent(eventName, details) {
    var event = null;
    var detail = { detail: details || null };
    if (isFunction(CustomEvent)) { // Use CustomEvent constructor when available
        event = new CustomEvent(eventName, detail);
    }
    else { // CustomEvent has no constructor in IE
        var doc = getDocument();
        if (doc && doc[_DYN_CREATE_EVENT /* @min:%2ecreateEvent */]) {
            event = doc[_DYN_CREATE_EVENT /* @min:%2ecreateEvent */]("CustomEvent");
            event.initCustomEvent(eventName, true, true, detail);
        }
    }
    return event;
}
export function sendCustomEvent(evtName, cfg, customDetails) {
    var global = getGlobal();
    if (global && global.CustomEvent) {
        try {
            var details = { cfg: cfg || null, customDetails: customDetails || null };
            return dispatchEvent(global, createCustomDomEvent(evtName, details));
        }
        catch (e) {
            // eslint-disable-next-line no-empty
        }
    }
    return false;
}
/**
 * Redacts user information from a URL
 * @param url - The URL string to redact
 * @returns The URL with user information redacted
 */
/*#__NO_SIDE_EFFECTS__*/
function redactUserInfo(url) {
    return url.replace(/^([a-zA-Z][a-zA-Z0-9+.-]*:\/\/)([^:@]{1,200}):([^@]{1,200})@(.*)$/, "$1REDACTED:REDACTED@$4");
}
/**
 * Redacts sensitive query parameters from a URL
 * @param url - The URL string to redact
 * @returns The URL with sensitive query parameters redacted
 */
/*#__NO_SIDE_EFFECTS__*/
function redactQueryParameters(url, config) {
    var sensitiveParams;
    var questionMarkIndex = strIndexOf(url, "?");
    if (questionMarkIndex === -1) {
        return url;
    }
    var option = config ? config.redactUrls : undefined;
    if (option === 3 /* eUrlRedactionOptions.appendToDefault */) {
        sensitiveParams = DEFAULT_SENSITIVE_PARAMS[_DYN_CONCAT /* @min:%2econcat */](config.redactQueryParams);
    }
    else if (option === 4 /* eUrlRedactionOptions.replaceDefault */) {
        sensitiveParams = config.redactQueryParams;
    }
    else {
        sensitiveParams = DEFAULT_SENSITIVE_PARAMS;
    }
    var baseUrl = strSubstring(url, 0, questionMarkIndex + 1);
    var queryString = strSubstring(url, questionMarkIndex + 1);
    var fragment = STR_EMPTY;
    var hashIndex = strIndexOf(queryString, "#");
    if (hashIndex !== -1) {
        fragment = strSubstring(queryString, hashIndex);
        queryString = strSubstring(queryString, 0, hashIndex);
    }
    var hasPotentialSensitiveParam = false;
    for (var i = 0; i < sensitiveParams[_DYN_LENGTH /* @min:%2elength */]; i++) {
        var paramCheck = sensitiveParams[i] + "=";
        if (strIndexOf(queryString, paramCheck) !== -1) {
            hasPotentialSensitiveParam = true;
            break;
        }
    }
    if (!hasPotentialSensitiveParam) {
        return url;
    }
    var resultParts = [];
    var anyParamRedacted = false;
    if (queryString && queryString[_DYN_LENGTH /* @min:%2elength */]) {
        var pairs = queryString[_DYN_SPLIT /* @min:%2esplit */]("&");
        for (var i = 0; i < pairs[_DYN_LENGTH /* @min:%2elength */]; i++) {
            var pair = pairs[i];
            if (!pair) {
                continue;
            }
            var equalsIndex = strIndexOf(pair, "=");
            if (equalsIndex === -1) {
                // Parameter without value
                resultParts[_DYN_PUSH /* @min:%2epush */](pair);
            }
            else {
                var paramName = pair[_DYN_SUBSTRING /* @min:%2esubstring */](0, equalsIndex);
                var paramValue = pair[_DYN_SUBSTRING /* @min:%2esubstring */](equalsIndex + 1);
                if (paramValue === STR_EMPTY) {
                    resultParts[_DYN_PUSH /* @min:%2epush */](pair);
                }
                else {
                    var shouldRedact = false;
                    for (var j = 0; j < sensitiveParams[_DYN_LENGTH /* @min:%2elength */]; j++) {
                        if (paramName === sensitiveParams[j]) {
                            shouldRedact = true;
                            anyParamRedacted = true;
                            break;
                        }
                    }
                    if (shouldRedact) {
                        resultParts[_DYN_PUSH /* @min:%2epush */](paramName + "=" + STR_REDACTED);
                    }
                    else {
                        resultParts[_DYN_PUSH /* @min:%2epush */](pair);
                    }
                }
            }
        }
    }
    // If no parameters were redacted, return the original URL
    if (!anyParamRedacted) {
        return url;
    }
    return baseUrl + resultParts[_DYN_JOIN /* @min:%2ejoin */]("&") + fragment;
}
/**
 * Redacts sensitive information from a URL string, including credentials and specific query parameters.
 * @param input - The URL string to be redacted.
 * @param config - Configuration object that contain redactUrls setting.
 * @returns The redacted URL string or the original string if no redaction was needed or possible.
 */
/*#__NO_SIDE_EFFECTS__*/
export function fieldRedaction(input, config) {
    if (!input || !isString(input) || strIndexOf(input, " ") !== -1) {
        return input;
    }
    var option = config ? config.redactUrls : undefined;
    var isRedactionDisabled = option === false || option === 2 /* eUrlRedactionOptions.false */;
    if (isRedactionDisabled) {
        return input;
    }
    var hasCredentials = strIndexOf(input, "@") !== -1;
    var hasQueryParams = strIndexOf(input, "?") !== -1;
    // If no credentials and no query params, return original
    if (!hasCredentials && !hasQueryParams) {
        return input;
    }
    if (option === 5 /* eUrlRedactionOptions.usernamePasswordOnly */) {
        hasQueryParams = false;
    }
    if (option === 6 /* eUrlRedactionOptions.queryParamsOnly */) {
        hasCredentials = false;
    }
    try {
        var result = input;
        if (hasCredentials) {
            result = redactUserInfo(input);
        }
        if (hasQueryParams) {
            result = redactQueryParameters(result, config);
        }
        return result;
    }
    catch (e) {
        return input;
    }
}
//# sourceMappingURL=EnvUtils.js.map